import { z } from 'zod';
import { extendZodWithOpenApi } from '@asteasolutions/zod-to-openapi';
import { LANGUAGE_CODES, DEFAULT_LANGUAGE } from '../constants/languages.js';
import { MediaItemSchema, MediaTypeSchema as StepMediaTypeSchemaBase } from './media.schema.js';
// Extend Zod with OpenAPI
extendZodWithOpenApi(z);
// Tiptap JSON document schema — loose validation, deep structure validated by Tiptap
export const TiptapDocSchema = z.object({
    type: z.literal('doc'),
    content: z.array(z.unknown()).min(1, 'Prompt content is required'),
}).passthrough();
// Helper for cloze cross-validation
export function tiptapDocContainsClozeBlank(doc, blankId) {
    const walk = (node) => {
        if (!node || typeof node !== 'object')
            return false;
        const n = node;
        if (n.type === 'clozeBlank' && n.attrs?.id === blankId)
            return true;
        if (Array.isArray(n.content))
            return n.content.some(walk);
        return false;
    };
    return walk(doc);
}
// Step type enum (lowercase to match database)
export const StepTypeSchema = z
    .enum(['info', 'text', 'mcq', 'true_false', 'cloze', 'cloze_diagram', 'cloze_image', 'ranking', 'classify', 'image_hotspot', 'numeric'])
    .openapi({
    description: 'Type of learning step',
    example: 'text',
});
/** All step types (for iteration). */
export const STEP_TYPES = StepTypeSchema.options;
/** Question step types — all step types except 'info' (which has no interaction). */
export const QUESTION_STEP_TYPES = STEP_TYPES.filter((t) => t !== 'info');
// Step category enum
export const StepCategorySchema = z.enum(['learn', 'revise']).openapi({
    description: 'Category of step — learn or revise',
    example: 'learn',
});
// Step media type enum — re-exported from media.schema for backwards compatibility.
export const StepMediaTypeSchema = StepMediaTypeSchemaBase;
// Step media response schema — array of MediaItem objects (from join table).
// Signed URLs are generated at the API boundary before responding.
export const StepMediaSchema = z.array(MediaItemSchema).nullable().optional();
// Step media input schema — minimal shape for creating/updating media links.
// Used in CreateStepSchema and UpdateStepSchema (input from frontend).
export const StepMediaInputSchema = z
    .array(z.object({
    mediaId: z.string().uuid(),
    role: z.string().optional().default('primary_image'),
}))
    .nullable()
    .optional();
// Diagram schema -- structured text diagram (Mermaid)
export const DiagramTypeSchema = z
    .enum(['flowchart', 'sequence', 'state', 'mindmap', 'timeline', 'class'])
    .openapi({
    description: 'Type of Mermaid diagram',
    example: 'flowchart',
});
export const DiagramSchema = z
    .object({
    syntax: z
        .string()
        .min(1, 'Diagram syntax is required')
        .max(10000, 'Diagram syntax too long'),
    diagramType: DiagramTypeSchema,
})
    .openapi({
    description: 'Mermaid diagram definition',
});
// UUID validation
const uuidSchema = z.string().uuid('Invalid UUID format');
/**
 * Type-specific config schemas
 */
// AI Grading config (for text steps)
const AiGradingRubricItemSchema = z.object({
    text: z
        .string()
        .min(1)
        .openapi({ description: 'Rubric item text', example: 'Uses correct terminology' }),
    weight: z
        .number()
        .min(0)
        .max(1)
        .optional()
        .openapi({ description: 'Relative weight of this rubric item (0-1)', example: 0.5 }),
});
export const AiGradingConfigSchema = z.object({
    enabled: z
        .boolean()
        .default(true)
        .openapi({ description: 'Whether AI grading is enabled', example: true }),
    mode: z.enum(['hybrid', 'ai_only', 'exact_only']).default('hybrid').openapi({
        description: 'Grading mode — hybrid uses both exact match and AI',
        example: 'hybrid',
    }),
    feedbackStyle: z
        .enum(['brief', 'coaching', 'socratic'])
        .default('coaching')
        .openapi({ description: 'Style of AI-generated feedback', example: 'coaching' }),
    rubric: z
        .array(AiGradingRubricItemSchema)
        .optional()
        .openapi({ description: 'Optional grading rubric items with weights' }),
    minConfidence: z
        .number()
        .min(0)
        .max(1)
        .default(0.6)
        .openapi({ description: 'Minimum AI confidence threshold (0-1)', example: 0.6 }),
    maxOutputTokens: z
        .number()
        .int()
        .positive()
        .max(1000)
        .default(200)
        .openapi({ description: 'Maximum tokens for AI feedback output', example: 200 }),
});
// TEXT config
export const TextConfigSchema = z
    .object({
    acceptedAnswers: z
        .array(z.string().min(1))
        .min(1, 'At least one accepted answer is required')
        .openapi({ description: 'List of accepted correct answers' }),
    caseSensitive: z
        .boolean()
        .default(false)
        .openapi({ description: 'Whether answer matching is case-sensitive', example: false }),
    trimWhitespace: z
        .boolean()
        .default(true)
        .openapi({ description: 'Whether to trim whitespace before comparing', example: true }),
    placeholder: z.string().optional().openapi({
        description: 'Placeholder text shown in the input field',
        example: 'Type your answer here...',
    }),
    minLength: z
        .number()
        .int()
        .nonnegative()
        .optional()
        .openapi({ description: 'Minimum required answer length' }),
    maxLength: z
        .number()
        .int()
        .positive()
        .optional()
        .openapi({ description: 'Maximum allowed answer length' }),
    aiGrading: AiGradingConfigSchema.optional().openapi({
        description: 'Optional AI grading configuration',
    }),
})
    .refine((data) => {
    if (data.minLength !== undefined && data.maxLength !== undefined) {
        return data.minLength <= data.maxLength;
    }
    return true;
}, { message: 'minLength must be less than or equal to maxLength' })
    .passthrough();
// MCQ option (Choice)
export const ChoiceSchema = z.object({
    id: z.string().openapi({ description: 'Unique choice identifier', example: 'opt-1' }),
    label: z.string().openapi({ description: 'Display text for this choice', example: 'Option A' }),
});
// MCQ option
const McqOptionSchema = z.object({
    id: z.string().openapi({ description: 'Unique option identifier', example: 'opt-1' }),
    text: z
        .string()
        .min(1, 'Option text is required')
        .openapi({ description: 'Display text for this option', example: 'Photosynthesis' }),
    isCorrect: z
        .boolean()
        .openapi({ description: 'Whether this option is a correct answer', example: true }),
});
// MCQ config
export const McqConfigSchema = z
    .object({
    options: z
        .array(McqOptionSchema)
        .min(2, 'At least 2 options required')
        .openapi({ description: 'List of answer options (min 2)' }),
    allowMultiple: z
        .boolean()
        .optional()
        .openapi({ description: 'Whether multiple options can be selected', example: false }),
    shuffleOptions: z
        .boolean()
        .optional()
        .openapi({ description: 'Whether to shuffle option order on display', example: true }),
    tags: z
        .array(z.string())
        .optional()
        .openapi({ description: 'Optional tags for categorising the question' }),
})
    .passthrough()
    .refine((data) => data.options.some((o) => o.isCorrect), {
    message: 'At least one option must be marked as correct',
    path: ['options'],
});
// TRUE_FALSE config
export const TrueFalseConfigSchema = z
    .object({
    correctAnswer: z
        .boolean()
        .openapi({ description: 'The correct answer (true or false)', example: true }),
    tags: z
        .array(z.string())
        .optional()
        .openapi({ description: 'Optional tags for categorising the question' }),
})
    .passthrough();
// CLOZE blank schema (mode is at config level, not per-blank)
export const ClozeBlankSchema = z.object({
    id: z.string().uuid('Invalid blank ID').openapi({
        description: 'Unique blank identifier (UUID)',
        example: '550e8400-e29b-41d4-a716-446655440000',
    }),
    index: z
        .number()
        .int()
        .nonnegative()
        .openapi({ description: 'Position index of this blank in the prompt', example: 0 }),
    // free_text: each blank has its own accepted answers
    acceptedAnswers: z
        .array(z.string().min(1))
        .optional()
        .openapi({ description: 'Accepted answers for free-text mode' }),
    // multiple_choice: each blank has its own correct answer
    correctAnswer: z
        .string()
        .optional()
        .openapi({ description: 'Correct answer for multiple-choice mode' }),
});
// CLOZE config — mode + settings are question-level, not per-blank
export const ClozeConfigSchema = z
    .object({
    mode: z
        .enum(['free_text', 'multiple_choice'])
        .openapi({ description: 'Cloze interaction mode', example: 'free_text' }),
    blanks: z
        .array(ClozeBlankSchema)
        .min(1, 'At least one blank is required')
        .openapi({ description: 'List of blanks within the prompt' }),
    // free_text question-level settings
    caseSensitive: z
        .boolean()
        .optional()
        .openapi({ description: 'Whether free-text matching is case-sensitive', example: false }),
    trimWhitespace: z
        .boolean()
        .optional()
        .openapi({ description: 'Whether to trim whitespace before comparing', example: true }),
    // multiple_choice question-level settings
    distractors: z
        .array(z.string().min(1))
        .optional()
        .openapi({ description: 'Distractor options for multiple-choice mode' }),
    shuffle: z
        .boolean()
        .optional()
        .openapi({ description: 'Whether to shuffle multiple-choice options', example: true }),
    aiGrading: AiGradingConfigSchema.optional().openapi({
        description: 'Optional AI grading config (free_text mode only)',
    }),
})
    .superRefine((config, ctx) => {
    if (config.mode === 'free_text') {
        for (const [i, blank] of config.blanks.entries()) {
            if (!blank.acceptedAnswers || blank.acceptedAnswers.length === 0) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `Blank ${i + 1} requires at least one accepted answer`,
                    path: ['blanks', i, 'acceptedAnswers'],
                });
            }
        }
    }
    else {
        for (const [i, blank] of config.blanks.entries()) {
            if (!blank.correctAnswer || blank.correctAnswer.trim().length === 0) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `Blank ${i + 1} requires a correct answer`,
                    path: ['blanks', i, 'correctAnswer'],
                });
            }
        }
        if (!config.distractors || config.distractors.length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Multiple-choice mode requires at least one distractor',
                path: ['distractors'],
            });
        }
    }
    // AI grading is only valid for free_text mode
    if (config.mode === 'multiple_choice' && config.aiGrading) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'AI grading is only supported for free_text cloze mode',
            path: ['aiGrading'],
        });
    }
})
    .passthrough();
// CLOZE DIAGRAM blank schema (blanks reference Mermaid node IDs)
const ClozeDiagramBlankSchema = z.object({
    id: uuidSchema.openapi({ description: 'Unique blank identifier (UUID)' }),
    nodeId: z.string().min(1, 'Node ID is required').openapi({
        description: 'Mermaid node ID to blank out',
        example: 'B',
    }),
    position: z.number().int().nonnegative().openapi({
        description: 'Fill order (0-indexed)',
        example: 0,
    }),
    acceptedAnswers: z
        .array(z.string().min(1))
        .optional()
        .openapi({ description: 'Accepted answers for free-text mode' }),
    correctAnswer: z.string().optional().openapi({
        description: 'Correct answer for multiple-choice mode',
    }),
});
// CLOZE DIAGRAM config — cloze-style interaction over a Mermaid diagram
export const ClozeDiagramConfigSchema = z
    .object({
    mode: z.enum(['free_text', 'multiple_choice']).openapi({
        description: 'Blank interaction mode',
        example: 'multiple_choice',
    }),
    blanks: z
        .array(ClozeDiagramBlankSchema)
        .min(1, 'At least one blank is required')
        .openapi({ description: 'Blanked-out diagram nodes' }),
    distractors: z
        .array(z.string().min(1))
        .optional()
        .openapi({ description: 'Distractor options for MC mode' }),
    shuffle: z.boolean().optional().default(true).openapi({
        description: 'Shuffle option order for MC mode',
    }),
    caseSensitive: z.boolean().optional().default(false),
    trimWhitespace: z.boolean().optional().default(true),
    aiGrading: AiGradingConfigSchema.optional(),
})
    .superRefine((data, ctx) => {
    for (let i = 0; i < data.blanks.length; i++) {
        const blank = data.blanks[i];
        if (data.mode === 'free_text') {
            if (!blank.acceptedAnswers || blank.acceptedAnswers.length === 0) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `Free-text blank at position ${blank.position} requires acceptedAnswers`,
                    path: ['blanks', i, 'acceptedAnswers'],
                });
            }
        }
        else {
            if (!blank.correctAnswer) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `MC blank at position ${blank.position} requires correctAnswer`,
                    path: ['blanks', i, 'correctAnswer'],
                });
            }
        }
    }
    if (data.mode === 'multiple_choice') {
        if (!data.distractors || data.distractors.length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Multiple-choice mode requires at least one distractor',
                path: ['distractors'],
            });
        }
    }
    if (data.mode === 'multiple_choice' && data.aiGrading) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'AI grading is only supported for free-text mode',
            path: ['aiGrading'],
        });
    }
    const nodeIds = data.blanks.map((b) => b.nodeId);
    if (new Set(nodeIds).size !== nodeIds.length) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Duplicate nodeId in blanks',
            path: ['blanks'],
        });
    }
    const positions = data.blanks.map((b) => b.position);
    if (new Set(positions).size !== positions.length) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Duplicate position in blanks',
            path: ['blanks'],
        });
    }
})
    .openapi({ description: 'Configuration for cloze diagram step type' });
// RANKING option
export const RankingOptionSchema = z.object({
    id: z.string().openapi({ description: 'Unique ranking option identifier', example: 'rank-1' }),
    text: z.string().min(1, 'Option text is required').openapi({
        description: 'Display text for this ranking option',
        example: 'Step 1: Gather materials',
    }),
});
// RANKING config
export const RankingConfigSchema = z
    .object({
    options: z
        .array(RankingOptionSchema)
        .min(3, 'At least 3 options required')
        .max(10, 'At most 10 options allowed')
        .openapi({ description: 'Ranking options in their correct order (3-10)' }),
    shuffleOptions: z
        .boolean()
        .optional()
        .openapi({ description: 'Whether to shuffle option order on display', example: true }),
})
    .passthrough();
// CLASSIFY category
export const ClassifyCategorySchema = z.object({
    id: z.string().openapi({ description: 'Unique category identifier', example: 'cat-1' }),
    label: z.string().min(1, 'Category label is required').openapi({
        description: 'Display label for this category',
        example: "Crohn's Disease",
    }),
});
// CLASSIFY item
export const ClassifyItemSchema = z.object({
    id: z.string().openapi({ description: 'Unique item identifier', example: 'item-1' }),
    text: z.string().min(1, 'Item text is required').openapi({
        description: 'Display text for this item',
        example: 'Perianal lesions',
    }),
    categoryId: z.string().openapi({
        description: 'ID of the correct category this item belongs to',
        example: 'cat-1',
    }),
});
// CLASSIFY config
export const ClassifyConfigSchema = z
    .object({
    categories: z
        .array(ClassifyCategorySchema)
        .min(2, 'At least 2 categories required')
        .max(3, 'At most 3 categories allowed')
        .openapi({ description: 'Classification categories (2-3)' }),
    items: z
        .array(ClassifyItemSchema)
        .min(3, 'At least 3 items required')
        .max(12, 'At most 12 items allowed')
        .openapi({ description: 'Items to classify (3-12)' }),
    shuffleItems: z
        .boolean()
        .optional()
        .openapi({ description: 'Whether to shuffle item order on display', example: true }),
    shuffleCategories: z
        .boolean()
        .optional()
        .openapi({ description: 'Whether to shuffle category order on display', example: false }),
})
    .superRefine((config, ctx) => {
    const categoryIds = new Set(config.categories.map((c) => c.id));
    for (const [i, item] of config.items.entries()) {
        if (!categoryIds.has(item.categoryId)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Item "${item.text}" references unknown category ID: ${item.categoryId}`,
                path: ['items', i, 'categoryId'],
            });
        }
    }
})
    .passthrough();
// ─── Image Hotspot ──────────────────────────────────────────────────────────
export const HotspotRegionSchema = z
    .object({
    id: z.string().openapi({ description: 'Unique region identifier' }),
    label: z.string().openapi({ description: 'Region label, e.g. "poodle"' }),
    points: z
        .array(z.object({
        x: z.number().min(0).max(1).openapi({ description: 'Normalised X (0-1)' }),
        y: z.number().min(0).max(1).openapi({ description: 'Normalised Y (0-1)' }),
    }))
        .min(3)
        .openapi({ description: 'Polygon vertices in normalised 0-1 coordinates' }),
    isCorrect: z.boolean().openapi({ description: 'Whether clicking this region is a correct answer' }),
})
    .passthrough()
    .openapi('HotspotRegion');
export const ImageHotspotConfigSchema = z
    .object({
    regions: z
        .array(HotspotRegionSchema)
        .min(2)
        .openapi({ description: 'Clickable image regions (min 2)' }),
    targetDescription: z
        .string()
        .optional()
        .openapi({ description: 'Semantic hint for AI: what the learner should find' }),
})
    .passthrough()
    .refine((c) => c.regions.some((r) => r.isCorrect), {
    message: 'At least one region must be marked as correct',
})
    .openapi('ImageHotspotConfig');
// ─── Cloze Image ───────────────────────────────────────────────────────────
/** A labelling blank placed at a specific point on an image. */
export const ClozeImageBlankSchema = z.object({
    id: z.string().openapi({ description: 'Unique blank identifier' }),
    x: z.number().min(0).max(1).openapi({ description: 'Normalised X position of marker (0-1)' }),
    y: z.number().min(0).max(1).openapi({ description: 'Normalised Y position of marker (0-1)' }),
    width: z
        .number()
        .min(0)
        .max(1)
        .optional()
        .openapi({ description: 'Width of label cover region (0-1), present when covering existing text' }),
    height: z
        .number()
        .min(0)
        .max(1)
        .optional()
        .openapi({ description: 'Height of label cover region (0-1), present when covering existing text' }),
    acceptedAnswers: z
        .array(z.string().min(1))
        .optional()
        .openapi({ description: 'Accepted answers for free-text mode (includes abbreviation + full form)' }),
    correctAnswer: z.string().optional().openapi({
        description: 'Correct answer for multiple-choice mode',
    }),
});
export const ClozeImageConfigSchema = z
    .object({
    mode: z.enum(['free_text', 'multiple_choice']).openapi({
        description: 'Blank interaction mode',
        example: 'multiple_choice',
    }),
    blanks: z
        .array(ClozeImageBlankSchema)
        .min(2, 'At least 2 blanks required')
        .max(6, 'At most 6 blanks allowed')
        .openapi({ description: 'Labelling blanks positioned on the image' }),
    distractors: z
        .array(z.string().min(1))
        .optional()
        .openapi({ description: 'Distractor options for MC mode' }),
    shuffle: z.boolean().optional().default(true).openapi({
        description: 'Shuffle option order for MC mode',
    }),
    caseSensitive: z.boolean().optional().default(false),
    trimWhitespace: z.boolean().optional().default(true),
    aiGrading: AiGradingConfigSchema.optional(),
})
    .superRefine((data, ctx) => {
    for (let i = 0; i < data.blanks.length; i++) {
        const blank = data.blanks[i];
        if (data.mode === 'free_text') {
            if (!blank.acceptedAnswers || blank.acceptedAnswers.length === 0) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `Free-text blank ${blank.id} requires acceptedAnswers`,
                    path: ['blanks', i, 'acceptedAnswers'],
                });
            }
        }
        else {
            if (!blank.correctAnswer) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `MC blank ${blank.id} requires correctAnswer`,
                    path: ['blanks', i, 'correctAnswer'],
                });
            }
        }
    }
    if (data.mode === 'multiple_choice') {
        if (!data.distractors || data.distractors.length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Multiple-choice mode requires at least one distractor',
                path: ['distractors'],
            });
        }
    }
    if (data.mode === 'multiple_choice' && data.aiGrading) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'AI grading is only supported for free-text mode',
            path: ['aiGrading'],
        });
    }
})
    .openapi('ClozeImageConfig', { description: 'Configuration for cloze image (label an image) step type' });
// ─── Numeric ────────────────────────────────────────────────────────────────
// Numeric accepted answer entry
const NumericAcceptedAnswerSchema = z.object({
    value: z.number().openapi({ description: 'Accepted numeric value' }),
    unit: z.string().optional().openapi({ description: 'Unit for this accepted answer' }),
    tolerance: z
        .number()
        .nonnegative()
        .optional()
        .openapi({ description: 'Absolute tolerance override for this answer' }),
});
// Numeric config
export const NumericConfigSchema = z
    .object({
    correctAnswer: z
        .number()
        .optional()
        .openapi({ description: 'The correct numeric answer (exact answer mode)' }),
    tolerance: z
        .number()
        .nonnegative()
        .optional()
        .openapi({ description: 'Absolute tolerance: ±value', example: 0.5 }),
    tolerancePercent: z
        .number()
        .positive()
        .optional()
        .openapi({ description: 'Relative tolerance: ±% of correctAnswer', example: 5 }),
    acceptedRange: z
        .object({
        min: z.number().openapi({ description: 'Minimum accepted value' }),
        max: z.number().openapi({ description: 'Maximum accepted value' }),
    })
        .optional()
        .openapi({ description: 'Accepted range mode (mutually exclusive with correctAnswer)' }),
    acceptedAnswers: z
        .array(NumericAcceptedAnswerSchema)
        .optional()
        .openapi({ description: 'Additional accepted value+unit pairs' }),
    aiGrading: AiGradingConfigSchema.optional().openapi({
        description: 'Optional AI grading fallback for unit equivalences',
    }),
    partialCredit: z
        .boolean()
        .optional()
        .openapi({ description: 'Linear score decay within tolerance (correctAnswer mode only)' }),
    integerOnly: z
        .boolean()
        .optional()
        .openapi({ description: 'Only accept whole numbers', example: false }),
    decimalPlaces: z
        .number()
        .int()
        .nonnegative()
        .optional()
        .openapi({ description: 'Input precision hint' }),
    significantFigures: z
        .number()
        .int()
        .positive()
        .optional()
        .openapi({ description: 'Client-side sig fig validation' }),
    inputMode: z
        .enum(['field', 'stepper'])
        .optional()
        .openapi({ description: 'Input mode: field or stepper with +/- buttons' }),
    placeholder: z.string().optional().openapi({
        description: 'Placeholder text in the input field',
        example: 'Enter your answer',
    }),
    unit: z.string().optional().openapi({
        description: 'Display-only unit label',
        example: 'kg',
    }),
    unitPosition: z
        .enum(['prefix', 'suffix'])
        .optional()
        .openapi({ description: 'Unit position relative to input (default suffix)' }),
    unitOptions: z
        .array(z.string().min(1))
        .optional()
        .openapi({ description: 'Selectable unit dropdown options' }),
    correctUnit: z
        .string()
        .optional()
        .openapi({ description: 'Correct unit when unitOptions is set' }),
})
    .passthrough()
    .refine((data) => {
    const hasCorrectAnswer = data.correctAnswer !== undefined;
    const hasRange = data.acceptedRange !== undefined;
    return hasCorrectAnswer || hasRange;
}, { message: 'Either correctAnswer or acceptedRange is required' })
    .refine((data) => {
    const hasCorrectAnswer = data.correctAnswer !== undefined;
    const hasRange = data.acceptedRange !== undefined;
    return !(hasCorrectAnswer && hasRange);
}, { message: 'correctAnswer and acceptedRange are mutually exclusive' })
    .refine((data) => !(data.tolerance !== undefined && data.tolerancePercent !== undefined), { message: 'tolerance and tolerancePercent are mutually exclusive' })
    .refine((data) => {
    if (data.acceptedRange !== undefined) {
        return data.tolerance === undefined && data.tolerancePercent === undefined;
    }
    return true;
}, { message: 'tolerance/tolerancePercent only valid with correctAnswer, not acceptedRange' })
    .refine((data) => {
    if (data.tolerancePercent !== undefined && data.correctAnswer === 0)
        return false;
    return true;
}, { message: 'tolerancePercent cannot be used when correctAnswer is 0 (5% of 0 = 0)' })
    .refine((data) => {
    if (data.unitOptions && data.unitOptions.length > 0) {
        return data.correctUnit !== undefined && data.unitOptions.includes(data.correctUnit);
    }
    return true;
}, { message: 'correctUnit is required when unitOptions is set and must be in the array' })
    .refine((data) => {
    if (data.acceptedRange)
        return data.acceptedRange.min < data.acceptedRange.max;
    return true;
}, { message: 'acceptedRange.min must be less than acceptedRange.max' });
// INFO config (flexible, allows any fields)
export const InfoConfigSchema = z.record(z.string(), z.unknown()).nullable();
// Discriminated union for type-safe step config access.
// Narrow on `type` to get full type safety on `config`.
export const TypedStepConfigSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('mcq'), config: McqConfigSchema }),
    z.object({ type: z.literal('text'), config: TextConfigSchema }),
    z.object({ type: z.literal('true_false'), config: TrueFalseConfigSchema }),
    z.object({ type: z.literal('cloze'), config: ClozeConfigSchema }),
    z.object({ type: z.literal('cloze_diagram'), config: ClozeDiagramConfigSchema }),
    z.object({ type: z.literal('ranking'), config: RankingConfigSchema }),
    z.object({ type: z.literal('classify'), config: ClassifyConfigSchema }),
    z.object({ type: z.literal('image_hotspot'), config: ImageHotspotConfigSchema }),
    z.object({ type: z.literal('cloze_image'), config: ClozeImageConfigSchema }),
    z.object({ type: z.literal('numeric'), config: NumericConfigSchema }),
    z.object({ type: z.literal('info'), config: z.null() }),
]);
/**
 * Create step schema with type-specific validation
 */
export const CreateStepSchema = z
    .object({
    type: StepTypeSchema,
    category: StepCategorySchema.optional().default('learn'),
    prompt: TiptapDocSchema,
    config: z.unknown().nullable().optional(),
    media: StepMediaInputSchema,
    diagram: DiagramSchema.nullable().optional(),
    isLive: z.boolean().optional().default(true),
    points: z.number().int().nonnegative().optional(),
    explanation: z
        .string()
        .max(5000, 'Explanation must be less than 5000 characters')
        .nullable()
        .optional(),
})
    .transform((data) => {
    // Set default points based on type if not provided
    if (data.points === undefined) {
        data.points = data.type === 'info' ? 0 : 1;
    }
    return data;
})
    .superRefine((data, ctx) => {
    // Validate config based on type
    if (data.type === 'text') {
        if (!data.config) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'TEXT step requires config with acceptedAnswers',
                path: ['config'],
            });
            return;
        }
        const result = TextConfigSchema.safeParse(data.config);
        if (!result.success) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Invalid TEXT config: ${result.error.message}`,
                path: ['config'],
            });
        }
    }
    else if (data.type === 'mcq' && data.config) {
        const result = McqConfigSchema.safeParse(data.config);
        if (!result.success) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Invalid MCQ config: ${result.error.message}`,
                path: ['config'],
            });
        }
    }
    else if (data.type === 'true_false' && data.config) {
        const result = TrueFalseConfigSchema.safeParse(data.config);
        if (!result.success) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Invalid TRUE_FALSE config: ${result.error.message}`,
                path: ['config'],
            });
        }
    }
    else if (data.type === 'cloze') {
        if (!data.config) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'CLOZE step requires config with blanks',
                path: ['config'],
            });
            return;
        }
        const result = ClozeConfigSchema.safeParse(data.config);
        if (!result.success) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Invalid CLOZE config: ${result.error.message}`,
                path: ['config'],
            });
            return;
        }
        // Verify every blank ID appears in the prompt as a clozeBlank node
        const blanks = data.config && typeof data.config === 'object' && 'blanks' in data.config
            ? data.config.blanks || []
            : [];
        for (const blank of blanks) {
            const b = blank;
            if (!tiptapDocContainsClozeBlank(data.prompt, b.id)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `Blank ${b.id} not found in prompt as clozeBlank node`,
                    path: ['prompt'],
                });
            }
        }
    }
    else if (data.type === 'ranking') {
        if (!data.config) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'RANKING step requires config with options',
                path: ['config'],
            });
            return;
        }
        const result = RankingConfigSchema.safeParse(data.config);
        if (!result.success) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Invalid RANKING config: ${result.error.message}`,
                path: ['config'],
            });
        }
    }
    else if (data.type === 'classify') {
        if (!data.config) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'CLASSIFY step requires config with categories and items',
                path: ['config'],
            });
            return;
        }
        const result = ClassifyConfigSchema.safeParse(data.config);
        if (!result.success) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Invalid CLASSIFY config: ${result.error.message}`,
                path: ['config'],
            });
        }
    }
    else if (data.type === 'cloze_diagram') {
        if (!data.config) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'CLOZE_DIAGRAM step requires config with blanks',
                path: ['config'],
            });
            return;
        }
        const result = ClozeDiagramConfigSchema.safeParse(data.config);
        if (!result.success) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Invalid CLOZE_DIAGRAM config: ${result.error.message}`,
                path: ['config'],
            });
            return;
        }
        if (!data.diagram) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'CLOZE_DIAGRAM step requires a diagram',
                path: ['diagram'],
            });
            return;
        }
        if (data.diagram.diagramType !== 'flowchart') {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Only flowchart diagrams are supported for cloze_diagram (v1)',
                path: ['diagram', 'diagramType'],
            });
        }
    }
    else if (data.type === 'image_hotspot') {
        if (!data.config) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'IMAGE_HOTSPOT step requires config with regions',
                path: ['config'],
            });
            return;
        }
        const result = ImageHotspotConfigSchema.safeParse(data.config);
        if (!result.success) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Invalid IMAGE_HOTSPOT config: ${result.error.message}`,
                path: ['config'],
            });
        }
    }
    else if (data.type === 'cloze_image') {
        if (!data.config) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'CLOZE_IMAGE step requires config with blanks',
                path: ['config'],
            });
            return;
        }
        const result = ClozeImageConfigSchema.safeParse(data.config);
        if (!result.success) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Invalid CLOZE_IMAGE config: ${result.error.message}`,
                path: ['config'],
            });
        }
    }
    else if (data.type === 'numeric') {
        if (!data.config) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'NUMERIC step requires config with correctAnswer or acceptedRange',
                path: ['config'],
            });
            return;
        }
        const result = NumericConfigSchema.safeParse(data.config);
        if (!result.success) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Invalid NUMERIC config: ${result.error.message}`,
                path: ['config'],
            });
        }
    }
    // Diagram and media are mutually exclusive (all step types)
    if (data.diagram && data.media) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'A step cannot have both a diagram and media — choose one',
            path: ['diagram'],
        });
    }
});
/**
 * Update step schema (all fields optional)
 */
export const UpdateStepSchema = z
    .object({
    type: StepTypeSchema.optional(),
    category: StepCategorySchema.optional(),
    prompt: TiptapDocSchema.optional(),
    config: z.unknown().nullable().optional(),
    media: StepMediaInputSchema,
    diagram: DiagramSchema.nullable().optional(),
    isLive: z.boolean().optional(),
    points: z.number().int().nonnegative().optional(),
    explanation: z
        .string()
        .max(5000, 'Explanation must be less than 5000 characters')
        .nullable()
        .optional(),
})
    .superRefine((data, ctx) => {
    if (data.type === 'text' && data.config) {
        const result = TextConfigSchema.safeParse(data.config);
        if (!result.success) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Invalid TEXT config: ${result.error.message}`,
                path: ['config'],
            });
        }
    }
    else if (data.type === 'mcq' && data.config) {
        const result = McqConfigSchema.safeParse(data.config);
        if (!result.success) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Invalid MCQ config: ${result.error.message}`,
                path: ['config'],
            });
        }
    }
    else if (data.type === 'true_false' && data.config) {
        const result = TrueFalseConfigSchema.safeParse(data.config);
        if (!result.success) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Invalid TRUE_FALSE config: ${result.error.message}`,
                path: ['config'],
            });
        }
    }
    else if (data.type === 'cloze' && data.config) {
        const result = ClozeConfigSchema.safeParse(data.config);
        if (!result.success) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Invalid CLOZE config: ${result.error.message}`,
                path: ['config'],
            });
            return;
        }
        // Verify every blank ID appears in the prompt as a clozeBlank node
        if (data.prompt) {
            const blanks = data.config && typeof data.config === 'object' && 'blanks' in data.config
                ? data.config.blanks || []
                : [];
            for (const blank of blanks) {
                const b = blank;
                if (!tiptapDocContainsClozeBlank(data.prompt, b.id)) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `Blank ${b.id} not found in prompt as clozeBlank node`,
                        path: ['prompt'],
                    });
                }
            }
        }
    }
    else if (data.type === 'ranking' && data.config) {
        const result = RankingConfigSchema.safeParse(data.config);
        if (!result.success) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Invalid RANKING config: ${result.error.message}`,
                path: ['config'],
            });
        }
    }
    else if (data.type === 'classify' && data.config) {
        const result = ClassifyConfigSchema.safeParse(data.config);
        if (!result.success) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Invalid CLASSIFY config: ${result.error.message}`,
                path: ['config'],
            });
        }
    }
    else if (data.type === 'cloze_diagram' && data.config) {
        const result = ClozeDiagramConfigSchema.safeParse(data.config);
        if (!result.success) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Invalid CLOZE_DIAGRAM config: ${result.error.message}`,
                path: ['config'],
            });
            return;
        }
        if (data.diagram && data.diagram.diagramType !== 'flowchart') {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Only flowchart diagrams are supported for cloze_diagram (v1)',
                path: ['diagram', 'diagramType'],
            });
        }
    }
    else if (data.type === 'image_hotspot' && data.config) {
        const result = ImageHotspotConfigSchema.safeParse(data.config);
        if (!result.success) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Invalid IMAGE_HOTSPOT config: ${result.error.message}`,
                path: ['config'],
            });
        }
    }
    else if (data.type === 'cloze_image' && data.config) {
        const result = ClozeImageConfigSchema.safeParse(data.config);
        if (!result.success) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Invalid CLOZE_IMAGE config: ${result.error.message}`,
                path: ['config'],
            });
        }
    }
    else if (data.type === 'numeric' && data.config) {
        const result = NumericConfigSchema.safeParse(data.config);
        if (!result.success) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Invalid NUMERIC config: ${result.error.message}`,
                path: ['config'],
            });
        }
    }
    // Diagram and media are mutually exclusive (all step types)
    if (data.diagram && data.media) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'A step cannot have both a diagram and media — choose one',
            path: ['diagram'],
        });
    }
});
/**
 * List steps query parameters
 */
export const ListStepsQuerySchema = z.object({
    page: z
        .string()
        .optional()
        .default('1')
        .transform((val) => parseInt(val, 10))
        .refine((val) => val > 0, 'Page must be greater than 0'),
    pageSize: z
        .string()
        .optional()
        .default('10')
        .transform((val) => parseInt(val, 10))
        .refine((val) => val > 0 && val <= 100, 'Page size must be between 1 and 100'),
    sortBy: z
        .enum(['createdAt', 'updatedAt', 'type', 'points'])
        .optional()
        .default('createdAt'),
    sortOrder: z.enum(['asc', 'desc']).optional().default('desc'),
    type: StepTypeSchema.optional(),
    lessonId: uuidSchema.optional(),
    minPoints: z
        .string()
        .optional()
        .transform((val) => (val ? parseInt(val, 10) : undefined))
        .refine((val) => val === undefined || val >= 0, 'Must be non-negative'),
    maxPoints: z
        .string()
        .optional()
        .transform((val) => (val ? parseInt(val, 10) : undefined))
        .refine((val) => val === undefined || val >= 0, 'Must be non-negative'),
});
/**
 * Admin steps list query parameters (broader filtering for admin step browser)
 */
export const ListAdminStepsQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
    sortBy: z.enum(['prompt', 'createdAt', 'updatedAt', 'version', 'type', 'bloomsLevel', 'pctCorrect', 'avgTimeMs']).default('createdAt'),
    sortOrder: z.enum(['asc', 'desc']).default('desc'),
    search: z.string().optional(),
    stepType: StepTypeSchema.optional(),
    category: StepCategorySchema.optional(),
    isLive: z.enum(['true', 'false']).transform((v) => v === 'true').optional(),
    tagId: z.string().uuid().optional(),
    scope: z.enum(['mine']).optional(),
});
/**
 * Step ID parameter validation
 */
export const StepIdParamSchema = z.object({
    stepId: uuidSchema.openapi({
        description: 'Step UUID',
        example: '550e8400-e29b-41d4-a716-446655440000',
    }),
});
// Step Schema (response — includes content from version tables)
export const StepSchema = z
    .object({
    id: uuidSchema.openapi({
        description: 'Unique step identifier',
        example: '550e8400-e29b-41d4-a716-446655440000',
    }),
    type: StepTypeSchema,
    category: StepCategorySchema,
    prompt: TiptapDocSchema.openapi({
        description: 'The question or content prompt as a Tiptap JSON document',
    }),
    config: z
        .unknown()
        .nullable()
        .openapi({ description: 'Type-specific configuration (MCQ options, text answers, etc.)' }),
    media: StepMediaSchema.nullable().openapi({ description: 'Optional media attachment' }),
    diagram: DiagramSchema.nullable().openapi({ description: 'Optional Mermaid diagram' }),
    isLive: z.boolean().openapi({
        description: 'Whether the step is published and visible to learners',
        example: true,
    }),
    version: z.number().int().openapi({ description: 'Content version number', example: 1 }),
    createdAt: z.string().openapi({ description: 'Timestamp when the step was created' }),
    updatedAt: z.string().openapi({ description: 'Timestamp when the step was last updated' }),
    bloomsLevel: z
        .number()
        .int()
        .min(1)
        .max(6)
        .nullable()
        .openapi({ description: "Bloom's taxonomy level (1-6)", example: 3 }),
    points: z.coerce
        .number()
        .nullable()
        .openapi({ description: 'Points awarded for a correct answer', example: 1 }),
    explanation: z
        .string()
        .nullable()
        .openapi({ description: 'Explanation shown after the learner answers' }),
})
    .openapi('Step');
// ===== AI Step Generation Schemas =====
/**
 * Request body for POST /api/v1/steps/generate
 */
export const GenerateStepsRequestSchema = z.object({
    lessonId: z
        .string()
        .uuid('Invalid lesson UUID')
        .openapi({ description: 'Target lesson ID', example: '550e8400-e29b-41d4-a716-446655440000' }),
    category: StepCategorySchema.openapi({ description: 'Category for generated steps' }),
    prompt: z.string().max(10000, 'Prompt must be under 10 000 characters').default('').openapi({
        description: 'Natural language instructions for AI generation',
        example: 'Generate 5 questions about photosynthesis',
    }),
    stepTypes: z
        .array(StepTypeSchema)
        .min(1, 'At least one step type is required')
        .openapi({ description: 'Which step types to generate' }),
    stepCount: z
        .number()
        .int()
        .min(1)
        .max(50)
        .default(5)
        .openapi({ description: 'Number of steps to generate', example: 5 }),
    language: z
        .enum(LANGUAGE_CODES)
        .default(DEFAULT_LANGUAGE)
        .openapi({ description: 'Language code for generated content', example: 'en' }),
    mode: z
        .enum(['manual', 'extend', 'fromContent'])
        .default('manual')
        .openapi({
        description: 'Generation mode: manual prompt, extend existing lesson, or generate from content',
    }),
    fileContext: z
        .string()
        .max(50000, 'File context must be under 50 000 characters')
        .nullable()
        .optional()
        .openapi({ description: 'Optional file content to use as generation context' }),
    imageMode: z
        .enum(['off', 'on', 'force'])
        .default('off')
        .openapi({
        description: 'Image generation mode: off = no images, on = AI decides which steps get images, force = every step gets an image',
    }),
    courseVersionId: z
        .string()
        .uuid('Invalid course version UUID')
        .optional()
        .openapi({ description: 'Course version ID for draft-scoped step creation' }),
});
/**
 * Validates each AI-generated step object
 */
export const AiGeneratedStepSchema = z.object({
    type: StepTypeSchema.openapi({ description: 'Step type produced by the AI' }),
    prompt: z.string().openapi({ description: 'Generated question or content prompt' }),
    explanation: z
        .string()
        .nullable()
        .optional()
        .openapi({ description: 'Generated explanation for the correct answer' }),
    config: z
        .unknown()
        .nullable()
        .optional()
        .openapi({ description: 'Type-specific configuration generated by the AI' }),
    diagram: z
        .object({
        syntax: z.string(),
        diagramType: z.string(),
    })
        .optional()
        .openapi({ description: 'Optional Mermaid diagram — can be added to ANY step type, not just cloze_diagram' }),
    imageRef: z
        .string()
        .optional()
        .openapi({
        description: 'Reference to a document image the model can see (e.g. "img_1"). Use when a pre-extracted document image fits the step.',
    }),
    imageRequest: z
        .object({
        description: z.string(),
        importance: z.enum(['critical', 'beneficial', 'decorative']),
    })
        .optional()
        .openapi({
        description: 'Request for an image to be found or generated. The description should be specific. Importance controls resolution strategy.',
    }),
});
/**
 * Top-level AI generation output wrapper
 */
export const AiGenerationOutputSchema = z.object({
    steps: z
        .array(AiGeneratedStepSchema)
        .openapi({ description: 'Array of AI-generated step objects' }),
});
/**
 * Delete step media request body
 */
export const DeleteStepMediaSchema = z
    .object({
    stepId: z.string().uuid().openapi({
        description: 'Step that owns the media',
        example: '550e8400-e29b-41d4-a716-446655440000',
    }),
    mediaId: z.string().uuid().openapi({
        description: 'Media record to unlink',
        example: '550e8400-e29b-41d4-a716-446655440000',
    }),
})
    .openapi('DeleteStepMedia');
// Ranking item result (used in grading responses)
export const RankingItemResultSchema = z.object({
    optionId: z.string(),
    position: z.number(),
    isCorrect: z.boolean(),
    correctOptionId: z.string(),
});
// Blank result (used in cloze grading responses)
export const BlankResultSchema = z.object({
    isCorrect: z.boolean(),
    correctAnswer: z.string(),
});
// Classify item result (used in grading responses)
export const ClassifyItemResultSchema = z.object({
    itemId: z.string(),
    chosenCategoryId: z.string(),
    correctCategoryId: z.string(),
    isCorrect: z.boolean(),
});
//# sourceMappingURL=step.schema.js.map